import {
  UniversalNodeRegistry,
  type UniversalNodeSchema,
} from '@/features/backtest/schemas/universal-node-schema'
import { timeSlicePresets } from '@/features/backtest/schemas/time-slice-presets'

/**
 * A node's name as the canvas shows it. A user can give a universal node a
 * custom name (`data.customName`); its header then shows that name with the
 * node type's name small under it (Chris, 2026-10-28).
 *
 * Never `data.label`: every node stores one, set to the type's name when it was
 * added, and types have been renamed since (Universe became Stock Selection),
 * so an old strategy's label is not a name anyone chose.
 */

/** The longest custom name a node takes. */
export const CUSTOM_NAME_MAX_LENGTH = 40

/** Nodes that carry their own name or title (or have no header): never renamed. */
const OWN_TITLE_TYPES: ReadonlySet<string> = new Set([
  'universe',
  'subnet',
  'subnetInput',
  'subnetOutput',
  'group',
  'sticky',
  'reroute',
])

type NameSource = { customName?: unknown; preset?: unknown }

/** The trimmed, capped custom name; undefined when there is none. */
export function normalizeCustomName(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  const name = raw.trim().slice(0, CUSTOM_NAME_MAX_LENGTH).trim()
  return name || undefined
}

/** The node type's name, or its time-slice preset's: the small line under a custom name. */
export function nodeTypeName(
  data: NameSource,
  schema: Pick<UniversalNodeSchema, 'displayName'>,
): string {
  const preset = typeof data.preset === 'string' ? timeSlicePresets[data.preset] : undefined
  return preset?.displayName ?? schema.displayName
}

/** The custom name, else the type name. */
export function nodeDisplayName(
  data: NameSource,
  schema: Pick<UniversalNodeSchema, 'displayName'>,
): string {
  return normalizeCustomName(data.customName) ?? nodeTypeName(data, schema)
}

/** What a container or pill is called when it has no title of its own. */
const UNTITLED: Readonly<Record<string, string>> = {
  subnet: 'Subnet',
  group: 'Group',
  subnetInput: 'In',
  subnetOutput: 'Out',
}

/**
 * The name a person reads for any canvas node: in messages, panels, menus and
 * the run dialog. A subnet, group or subnet pill is called by its own title; a
 * universal node by its custom name, else its type's current name. The stored
 * label is the last resort, only for a node no registered schema can name.
 */
export function nodeName(
  node: { type?: string; id?: string; data?: unknown } | null | undefined,
): string {
  const data = (node?.data && typeof node.data === 'object' ? node.data : {}) as Record<
    string,
    unknown
  >
  const label = typeof data.label === 'string' ? data.label.trim() : ''
  // A migrated subnet's data.type may still say `group`: the node type decides.
  const kind = node?.type && node.type in UNTITLED ? node.type : undefined
  if (kind) return label || UNTITLED[kind]
  if (node?.type === 'reroute') return 'Reroute'
  if (node?.type === 'sticky') return 'Sticky note'

  const type = (typeof data.type === 'string' && data.type) || node?.type || ''
  const presetId = typeof data.presetId === 'string' ? data.presetId : undefined
  const schema =
    (presetId && UniversalNodeRegistry.getSchema(`${type}:${presetId}`)) ||
    UniversalNodeRegistry.getSchemaResolved(type)
  if (schema) return nodeDisplayName(data, schema)
  return normalizeCustomName(data.customName) ?? (label || type || node?.id || 'node')
}

/**
 * Changes whenever any node's custom name does, and on nothing else: a cache
 * of labels that name sources (a closed subnet card's rows) keys on it, since
 * a rename changes no wire. Cheap enough to run as a store selector.
 */
export function customNamesKey(nodes: ReadonlyArray<{ id: string; data?: unknown }>): string {
  let key = ''
  for (const node of nodes) {
    const name = normalizeCustomName(
      (node.data as { customName?: unknown } | undefined)?.customName,
    )
    if (name) key += `${node.id}\u0000${name}\u0001`
  }
  return key
}

/** Node data with the custom name set, or the key removed when the name is cleared. */
export function withCustomName<T extends Record<string, unknown>>(data: T, raw: unknown): T {
  const name = normalizeCustomName(raw)
  const { customName: _cleared, ...rest } = data
  return (name ? { ...rest, customName: name } : rest) as unknown as T
}

/** Whether the node can take a custom name: a universal node without a title of its own. */
export function canRenameNode(node: { type?: string; data?: { type?: unknown } | null }): boolean {
  const dataType = typeof node.data?.type === 'string' ? node.data.type : undefined
  const type = dataType ?? node.type
  if (!type) return false
  return !OWN_TITLE_TYPES.has(type) && !(node.type && OWN_TITLE_TYPES.has(node.type))
}
